import { ComponentType, useCallback, useMemo, useState } from 'react';

import { GearbeltEvent, PermissionRoles } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { IAccount, Products } from '@site-mate/sitemate-global-shared';
import { SitemateRegions } from '@site-mate/sitemate-infra-shared/models';
import { WorkspaceUrlService } from '@site-mate/sitemate-infra-shared/services';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';

import gearbeltLogoSmall from '@/assets/gearbelt-small.svg';
import placeholderLogo from '@/assets/placeholder-logo.png';
import { IconProps } from '@/components/icons';
import CalendarBlankIcon from '@/components/icons/calendar-blank';
import FormChecklistIcon from '@/components/icons/form-checklist';
import GearIcon from '@/components/icons/gear';
import HomeIcon from '@/components/icons/home';
import SkidSteerIcon from '@/components/icons/skid-steer';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import CreateWorkspace from '@/components/workspace-switcher/create-workspace';
import { hasAccountLayerAccess } from '@/components/workspace-switcher/workspace-switcher-menu.utils';
import { useWorkspaceCreatePermissions } from '@/hooks/use-workspace-create-permissions';
import { cn } from '@/lib/utils';
import { getCreateWorkspaceAccountQueryOptions, getWorkspaceSwitcherMenuQueryOptions } from '@/queries/workspace-switcher';
import { getAccountLayerPermissionsQueryOptions, workspaceSwitcherAccountKeys } from '@/queries/workspace-switcher-permissions';
import { accountService } from '@/services/global-api/account.service';
import { useCollapsibleNavbarStore } from '@/stores/collapsible-navbar.store';
import { useUserStore } from '@/stores/user.store';
import { useEvents } from '@/utils/events';
import { convertLogoUrl } from '@/utils/image-url';
import { toInfraProduct } from '@/utils/product';
import { getWorkspaceRoute } from '@/utils/routes';

import ClockIcon from '../icons/clock';
import DeploymentIcon from '../icons/deployment';
import FolderClosedIcon from '../icons/folder-closed';
import ListIcon from '../icons/list';
import MapIcon from '../icons/map';
import SidebarCollapseIcon from '../icons/sidebar-collapse';
import SidebarExpandIcon from '../icons/sidebar-expand';
import SwitchIcon from '../icons/switch';
import UsersIcon from '../icons/users';
import WorkspaceSwitcherMenu from '../workspace-switcher/workspace-switcher-menu';
import { groupWorkspacesIntoAccounts } from '../workspace-switcher/workspace-switcher-menu.utils';
import WorkspaceSwitcherTable from '../workspace-switcher/workspace-switcher-table';

export function Sidebar() {
  const { user, org } = useUserStore();
  const { isSidebarOpen, setIsSidebarOpen } = useCollapsibleNavbarStore();
  const toast = useToast();
  const [isChildHovered, setIsChildHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchTableOpen, setIsSearchTableOpen] = useState(false);
  const { trackEvent } = useEvents();

  const [createWorkspaceAccountId, setCreateWorkspaceAccountId] = useState<string | null>(null);

  const isSuperUser = user?.roles.includes(PermissionRoles.SitemateSuperUser) ?? false;

  const { data: createWorkspaceAccount } = useQuery(getCreateWorkspaceAccountQueryOptions({ accountId: createWorkspaceAccountId }));

  const {
    data: workspaces,
    isLoading: isLoadingWorkspaces,
    isError: isErrorWorkspaces,
  } = useQuery({
    ...getWorkspaceSwitcherMenuQueryOptions({ isSuperUser, accountId: org?.accountId }),
  });

  const accountGroups = useMemo(() => {
    if (!workspaces) return [];
    return groupWorkspacesIntoAccounts(workspaces);
  }, [workspaces]);

  const { permissionsByAccount, accountsByAccountId } = useWorkspaceCreatePermissions({
    accounts: accountGroups,
    isSuperUser,
    enabled: !isLoadingWorkspaces,
  });

  const [pendingAccountLayerId, setPendingAccountLayerId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const workspaceUrlService = useMemo(() => new WorkspaceUrlService(), []);

  const { data: accountLayerPermissions = [] } = useQuery({
    ...getAccountLayerPermissionsQueryOptions(),
    enabled: !isSuperUser,
  });

  const accountLayerEnabledByAccount = useMemo(() => {
    const result = new Map<string, boolean>();
    for (const account of accountGroups) {
      const accountData = accountsByAccountId.get(account.accountId);
      // isAccountLayerEnabled exists on the API response but is not yet typed in sitemate-global-shared
      const billing = accountData?.billing as IAccount['billing'] & { isAccountLayerEnabled?: boolean };
      result.set(account.accountId, billing?.isAccountLayerEnabled ?? false);
    }
    return result;
  }, [accountGroups, accountsByAccountId]);

  const accountLayerAccessByAccount = useMemo(() => {
    const result = new Map<string, boolean>();
    for (const account of accountGroups) {
      result.set(account.accountId, hasAccountLayerAccess(accountLayerPermissions, account.accountId, isSuperUser));
    }
    return result;
  }, [accountGroups, accountLayerPermissions, isSuperUser]);

  const generateDashpivotAccountUrl = useCallback(
    (accountId: string) => {
      const targetAccount = accountGroups.find((a) => a.accountId === accountId);
      const anyWorkspace = targetAccount?.products.flatMap((p) => p.workspaces)[0];
      const dashpivotUrl = workspaceUrlService.getWorkspaceUrl(window.location, {
        id: 'placeholder',
        product: toInfraProduct(Products.Dashpivot),
        region: (anyWorkspace?.region as SitemateRegions) ?? undefined,
        subdomain: anyWorkspace?.subdomain ?? undefined,
      });
      if (!dashpivotUrl) return null;
      const origin = new URL(dashpivotUrl).origin;
      return `${origin}/accounts/${accountId}/users`;
    },
    [accountGroups, workspaceUrlService],
  );

  const handleCreateSmsAccount = useCallback(
    async (accountId: string) => {
      setPendingAccountLayerId(accountId);
      try {
        await accountService.enableAccountLayer(accountId);
        trackEvent(GearbeltEvent.SitemateStartAccountLayerCreated, { Context: accountId });
        await queryClient.invalidateQueries({ queryKey: workspaceSwitcherAccountKeys.detail(accountId) });
        const url = generateDashpivotAccountUrl(accountId);
        if (url) {
          window.location.href = url;
        } else {
          setPendingAccountLayerId(null);
        }
      } catch {
        setPendingAccountLayerId(null);
        toast.error("We couldn't create the Sitemate Start Account layer. Try again, or contact support.");
      }
    },
    [trackEvent, queryClient, generateDashpivotAccountUrl, toast],
  );

  const handleSmsAccountSelected = useCallback(
    (accountId: string) => {
      setIsMenuOpen(false);
      const url = generateDashpivotAccountUrl(accountId);
      if (url) {
        window.location.href = url;
      }
    },
    [generateDashpivotAccountUrl],
  );

  const sidebarItems = useMemo(
    () =>
      [
        {
          type: 'section',
          label: 'Admin',
        },
        {
          type: 'link',
          icon: HomeIcon,
          label: 'Dashboard',
          href: '/dashboard',
        },
        {
          type: 'link',
          icon: SkidSteerIcon,
          label: 'Assets',
          href: '/assets',
        },
        {
          type: 'link' as const,
          icon: DeploymentIcon,
          label: 'Deployment',
          href: '/deployments',
        },
        {
          type: 'link',
          icon: UsersIcon,
          label: 'Users',
          href: '/users',
          alsoMatch: ['^/workspaces/[^/]+/users/.*', '^/workspaces/[^/]+/fleet/contacts/.*'],
        },
        {
          type: 'link',
          icon: MapIcon,
          label: 'Locations',
          href: '/locations',
          alsoMatch: ['^/workspaces/[^/]+/locations/.*'],
        },
        {
          type: 'link',
          icon: ListIcon,
          label: 'Lists',
          href: '/lists',
        },
        {
          type: 'link',
          icon: FolderClosedIcon,
          label: 'Documents',
          href: '/reporting/documents',
        },
        {
          type: 'link',
          icon: GearIcon,
          label: 'Settings',
          href: '/settings/workspace/general',
          alsoMatch: ['^/workspaces/[^/]+/settings/.*'],
        },
        {
          type: 'section',
          label: 'Management',
        },
        {
          type: 'link',
          icon: FormChecklistIcon,
          label: 'Inspections',
          href: '/inspections/activity',
          alsoMatch: ['^/workspaces/[^/]+/inspections/.*'],
        },
        {
          type: 'link',
          icon: WarningTriangleIcon,
          label: 'Issues',
          href: '/issues',
          alsoMatch: ['^/workspaces/[^/]+/issues/.*'],
        },
        {
          type: 'link' as const,
          icon: ClockIcon,
          label: 'Schedules',
          href: '/schedules/checks',
          alsoMatch: ['^/workspaces/[^/]+/schedules/.*', '^/workspaces/[^/]+/schedules', '^/workspaces/[^/]+/config/schedules/.*'],
        },
        {
          type: 'link',
          icon: CalendarBlankIcon,
          label: 'Work Orders',
          href: '/work-orders',
          alsoMatch: ['^/workspaces/[^/]+/work-orders/.*'],
        },
      ] satisfies { type: 'link' | 'section'; icon?: ComponentType<IconProps>; label: string; href?: string; alsoMatch?: string[] }[],
    [],
  );

  const handleCreateGearbeltWorkspace = useCallback((accountId: string) => {
    setIsMenuOpen(false);
    setCreateWorkspaceAccountId(accountId);
  }, []);

  const handleSwitchWorkspaceClick = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const handleSwitchAccount = useCallback(() => {
    setIsMenuOpen(false);
    setIsSearchTableOpen(true);
  }, []);

  const handleSidebarExpand = useCallback(() => {
    setIsSidebarOpen(!isSidebarOpen);
    setIsChildHovered(false);
    trackEvent(GearbeltEvent.Navigation, {
      Context: 'Updated',
    });
  }, [isSidebarOpen, setIsSidebarOpen, setIsChildHovered, trackEvent]);

  return (
    <div
      className={cn(
        'bg-default-text flex h-full flex-col items-start gap-1 p-2',
        isSidebarOpen ? 'w-[260px]' : 'w-[51px]',
        !isSidebarOpen && isChildHovered && 'bg-grey-9',
      )}>
      <div className={cn('text-grey-5 flex w-full items-center py-[5px]', isSidebarOpen ? 'justify-between' : 'flex-col justify-center')}>
        <div className="flex h-[30px] w-fit items-center justify-start gap-2 py-[5px]">
          <img src={gearbeltLogoSmall} alt="Gearbelt" className="h-[30px] w-[30px]" />
          <span aria-hidden="true" className={cn('gearbelt-logo-wordmark whitespace-nowrap', !isSidebarOpen && 'hidden')}>
            GEARBELT
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Collapse sidebar"
          className={cn('text-grey-5 hover:bg-grey-9 h-fit w-fit p-1', !isSidebarOpen && 'hidden')}
          onClick={handleSidebarExpand}>
          <SidebarCollapseIcon size={20} />
        </Button>
      </div>

      <div className={cn('flex w-full items-center justify-start gap-x-1', !isSidebarOpen && 'hidden')}>
        <img
          src={org?.logoUrl ? convertLogoUrl(org.logoUrl) : placeholderLogo}
          className={cn('h-[30px] max-w-[96px] min-w-[20px] rounded-sm border-2 border-white bg-white object-contain')}
        />
        <div className="truncate font-bold text-white">{org?.name}</div>
      </div>

      <div className={cn('flex w-full flex-col', !isSidebarOpen && 'hidden')}>
        <div
          onClick={handleSwitchWorkspaceClick}
          className="text-grey-5 hover:bg-grey-9 hover:text-grey-3 flex h-[30px] w-full items-center gap-1 rounded p-1 text-sm hover:cursor-pointer">
          <SwitchIcon size={20} /> <span>Switch workspace</span>
        </div>
      </div>
      {isMenuOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsMenuOpen(false)} />
          <div className={cn('fixed top-[88px] z-[9999]', isSidebarOpen ? 'left-[254px]' : 'left-[44px]')}>
            <WorkspaceSwitcherMenu
              accounts={accountGroups}
              isSuperUser={isSuperUser}
              currentAccountId={org?.accountId}
              isLoading={isLoadingWorkspaces}
              isError={isErrorWorkspaces}
              onSwitchAccount={handleSwitchAccount}
              permissionsByAccount={permissionsByAccount}
              onCreateGearbeltWorkspace={handleCreateGearbeltWorkspace}
              onClose={() => setIsMenuOpen(false)}
              accountLayerEnabledByAccount={accountLayerEnabledByAccount}
              accountLayerAccessByAccount={accountLayerAccessByAccount}
              pendingAccountLayerId={pendingAccountLayerId}
              onCreateSmsAccount={handleCreateSmsAccount}
              onSmsAccountSelected={handleSmsAccountSelected}
            />
          </div>
        </>
      )}

      <WorkspaceSwitcherTable isOpen={isSearchTableOpen} onClose={() => setIsSearchTableOpen(false)} />

      <ScrollArea className="w-full">
        <div className={cn('flex w-full flex-col gap-1 pb-4', !isSidebarOpen && 'w-[30px] items-center pb-0')}>
          {/* collapsed sidebar */}
          <Button
            variant="ghost"
            size="icon"
            aria-label="Expand sidebar"
            className={cn('text-grey-5 hover:bg-grey-9 h-fit w-fit px-2 py-1', !isSidebarOpen && 'px-1', isSidebarOpen && 'hidden')}
            onClick={handleSidebarExpand}>
            <SidebarExpandIcon size={20} />
          </Button>
          {!isSidebarOpen && (
            <div
              onClick={handleSwitchWorkspaceClick}
              className="text-grey-5 hover:bg-grey-9 hover:text-grey-3 flex w-fit items-center gap-1 rounded px-1 py-1 text-sm hover:cursor-pointer">
              <SwitchIcon size={20} />
            </div>
          )}

          {sidebarItems.map((item) => (
            <SidebarItem key={item.label} {...item} />
          ))}
        </div>
      </ScrollArea>

      {/* area to expand the sidebar, matches DP behaviour */}
      {!isSidebarOpen && (
        <div
          className="flex w-full flex-1 hover:cursor-pointer"
          onClick={handleSidebarExpand}
          onMouseEnter={() => setIsChildHovered(true)}
          onMouseLeave={() => setIsChildHovered(false)}></div>
      )}

      <Dialog open={!!createWorkspaceAccountId} onOpenChange={(open) => !open && setCreateWorkspaceAccountId(null)}>
        <DialogContent className="top-0 mt-25 w-[500px] border-white">
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
          </DialogHeader>
          <CreateWorkspace account={createWorkspaceAccount ?? null} isLoading={!createWorkspaceAccount} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SidebarItem({
  type,
  icon: Icon,
  label,
  href,
  alsoMatch = [],
}: {
  type: 'link' | 'section';
  icon?: ComponentType<IconProps>;
  label: string;
  href?: string;
  alsoMatch?: string[];
}) {
  const location = useLocation();
  const { org } = useUserStore();
  const { isSidebarOpen } = useCollapsibleNavbarStore();

  const actualHref = useMemo(() => {
    if (org) {
      return getWorkspaceRoute(href ?? '');
    }
    return '';
  }, [href, org]);

  const isActive = useMemo(() => {
    if (!actualHref) return false;

    const standardMatch = location.pathname === actualHref || (actualHref !== '/' && location.pathname.startsWith(actualHref));

    const additionalMatch = alsoMatch.some((pattern) => new RegExp(pattern).test(location.pathname));

    return standardMatch || additionalMatch;
  }, [actualHref, location.pathname, alsoMatch]);

  if (type === 'section') {
    return <div className={cn('text-secondary-text py-1 pl-1 text-xs font-medium uppercase', !isSidebarOpen && 'hidden')}>{label}</div>;
  }

  return (
    <Link
      to={actualHref ?? ''}
      className={cn(
        'text-grey-5 hover:bg-grey-9 hover:text-grey-3 flex items-center gap-1 rounded py-1 text-sm leading-none',
        isActive && '!bg-default-blue !text-white',
        isSidebarOpen ? 'w-full px-1' : 'w-fit px-1',
      )}>
      {!!Icon && <Icon size={20} />}
      <span className={cn(!isSidebarOpen && 'hidden')}>{label}</span>
    </Link>
  );
}
